import * as React from 'react';

import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { PriceBreakDownViewModel } from 'models';

import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';

export interface PriceBreakDownContentProps {
  priceBreakDownModel: PriceBreakDownViewModel[];
  view?: string;
  roomName?: string;
  childActions?: React.ReactNode;
}

export const PriceBreakDownContent: React.FunctionComponent<PriceBreakDownContentProps> = ({
  priceBreakDownModel,
  view,
  roomName,
  childActions
}) => {
  if (!priceBreakDownModel) {
    return null;
  }

  const {
    breakdownHeader,
    totalBasePriceLocalization,
    totalBasePriceDescription,
    totalTaxAndFeesLocalization,
    totalTaxAndFees,
    totalBasePrice,
    totalPriceDescription,
    totalPrice,
    excludedFromTotalDescription,
    bundledFees,
    unbundledFees,
    bookingLabel
  } = priceBreakDownModel[0];

  const separatorClasses = ['all-t-padding-four', 'uitk-cell all-cell-1-1', 'uitk-card-separator-top', 'pwa-theme--grey-700'];

  return (
    <>
      <ul data-stid="price-breakdown-container" className="no-bullet">
        <li className={`uitk-type-left all-x-padding-four ${view === 'small' ? 'all-t-margin-four' : 'all-t-margin-two'}`}>
          <PriceBreakDownTitle title={view === 'small' ? roomName : breakdownHeader} />
        </li>

        <PriceBreakDownRow
          data={{
            primaryCopy: totalBasePriceLocalization,
            secondaryCopy: totalBasePriceDescription,
            price: totalBasePrice
          }}
          view={view}
        />
        <PriceBreakDownRow
          data={{
            primaryCopy: totalTaxAndFeesLocalization,
            price: totalTaxAndFees
          }}
          view={view}
        />
        {bundledFees &&
          bundledFees.length > 0 &&
          bundledFees.map((listItem, index) => (
            <PriceBreakDownRow
              key={index}
              data={{
                primaryCopy: listItem.feeTypeDescription,
                secondaryCopy: listItem.detail,
                price: listItem.fee
              }}
              view={view}
            />
          ))}
        <PriceBreakDownRow
          data={{
            primaryCopy: totalPriceDescription,
            price: totalPrice,
            isBold: true
          }}
          view={view}
        />
        {unbundledFees &&
          unbundledFees.length > 0 && (
            <>
              <li className={`uitk-type-left all-x-padding-four ${view === 'small' ? 'all-t-margin-four' : 'all-t-margin-two'}`}>
                <div className={separatorClasses.join(' ')}>{excludedFromTotalDescription}</div>
              </li>
              {unbundledFees.map((listItem, index) => (
                <PriceBreakDownRow
                  key={index}
                  data={{
                    primaryCopy: listItem.feeTypeDescription,
                    secondaryCopy: listItem.detail,
                    price: listItem.fee
                  }}
                  view={view}
                />
              ))}
            </>
          )}
      </ul>

      {bookingLabel &&
        childActions && (
          <Viewport>
            <ViewSmall>
              <UitkScrim type={UitkScrimType.BUTTON}>{childActions}</UitkScrim>
            </ViewSmall>
            <ViewMedium>
              <>{childActions}</>
            </ViewMedium>
          </Viewport>
        )}
    </>
  );
};
PriceBreakDownContent['displayName'] = 'PriceBreakDownContent';

export interface PriceBreakDownTitleProps {
  title: string;
}
export const PriceBreakDownTitle: React.FC<PriceBreakDownTitleProps> = ({ title }: PriceBreakDownTitleProps) => (
  <div className="uitk-cell all-cell-1-1 uitk-type-300 uitk-type-bold">{title}</div>
);
PriceBreakDownTitle['displayName'] = 'PriceBreakDownTitle';

export interface PriceBreakDownRowProps {
  data: PriceBreakDownCellsProps;
  view?: string;
}

export const PriceBreakDownRow: React.FC<PriceBreakDownRowProps> = ({ data, view }: PriceBreakDownRowProps) => {
  const fontSize = view === 'small' ? 'uitk-type-300' : 'uitk-type-200';
  const marginTop = view === 'small' ? 'all-t-margin-four' : 'all-t-margin-two';

  return (
    <li className={`uitk-grid all-x-padding-four all-cell-1-2 uitk-type-left ${marginTop}`}>
      <PriceBreakDownCells
        primaryCopy={data.primaryCopy}
        secondaryCopy={data.secondaryCopy}
        price={data.price}
        isBold={data.isBold}
        fontSize={fontSize}
      />
    </li>
  );
};
PriceBreakDownRow['displayName'] = 'PriceBreakDownRow';

export interface PriceBreakDownCellsProps {
  primaryCopy: string;
  secondaryCopy?: string;
  price: string;
  isBold?: boolean;
  fontSize?: string;
}
export const PriceBreakDownCells: React.FC<PriceBreakDownCellsProps> = ({
  primaryCopy,
  secondaryCopy,
  price,
  isBold,
  fontSize
}: PriceBreakDownCellsProps) => {
  const commonStyles = 'uitk-cell';
  const fontType = isBold ? 'uitk-type-300 uitk-type-bold' : `${fontSize}`;
  const smallFont = fontSize === 'uitk-type-300' ? 'uitk-type-200' : 'uitk-type-100';

  return (
    <>
      <div className={`${commonStyles} all-cell-2-3  uitk-grid  ${fontType}`}>
        <div className="uitk-cell all-cell-1-1">{primaryCopy}</div>
        {secondaryCopy && <div className={`uitk-cell all-cell-1-1 ${smallFont} pwa-theme--grey-700`}>{secondaryCopy}</div>}
      </div>
      <div className={`${commonStyles} all-cell-1-3 ${fontType} uitk-type-right`}>{price}</div>
    </>
  );
};
PriceBreakDownCells['displayName'] = 'PriceBreakDownCells';
